import { CharacterType, IMPACT_SCORE_LABELS, ImpactScore } from "../types/book";
import { IssuePriority, Issue } from "../types/roadmapAnalysis";

export interface BarDataProps {
    label: string;
    percentage: number;
    impactLevel: "HIGH" | "MEDIUM" | "LOW";
}

/**
 * Normalizes smart quotes to ASCII quotes for robust JSON parsing.
 */
export function normalizeSmartQuotes(str: string): string {
    if (str == null) return str;
    return str.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
}

/**
 * Attempts to parse JSON with legacy fallback strategies for escaped quotes.
 */
function tryParseWithFallbacks(input: string, swallowErrors = false): any {
    input = removeNonPrintableCharacters(input);
    const attempts: string[] = [
        input,
        input.replace(/\\\\\"/g, '"'), // \\" => "
        input.replace(/\\\\\"/g, "'"), // \\" => '
        input.replace(/\\\"/g, '"'),     // \\" => "
        input.replace(/\\\"/g, "'"),     // \\" => '
        input.replace(/\"/g, '"'),         // \" => "
        input.replace(/\"/g, "'"),         // \" => '
    ];
    for (const attempt of attempts) {
        try {
            return normalizeQuotesDeep(JSON.parse(attempt));
        } catch (error) {
            // If this is the last attempt, log error context
            if (attempt === attempts[attempts.length - 1]) {
                if (
                    error &&
                    typeof error === 'object' &&
                    'message' in error &&
                    typeof (error as any).message === 'string'
                ) {
                    const message = (error as any).message as string;
                    const match = message.match(/at position (\d+)/);
                    if (match) {
                        const pos = parseInt(match[1], 10);
                        const before = attempt.slice(Math.max(0, pos - 10), pos);
                        const after = attempt.slice(pos, pos + 10);
                        console.error(
                            `JSON parse error at position ${pos}: ...${before}>>>${after}...`
                        );
                    }
                }
            }
        }
    }
    if (swallowErrors) {
        console.error("Failed to parse JSON with fallbacks");
        return null;
    } else {
        const parseError = new Error(`JSON parsing failed after all fallback attempts.`);
        (parseError as any).originalText = input;
        throw parseError;
    }
}



/**
 * Removes BOM, zero-width, and other non-printable/invisible Unicode characters from a string.
 */
export function removeNonPrintableCharacters(input: string): string {
    if (typeof input !== 'string') return input;
    // Remove BOM (U+FEFF), zero-width (U+200B-U+200D, U+2060, U+FEFF), and all C0/C1 control chars except \n, \r, \t
    return input
        .replace(/\uFEFF/g, '') // BOM
        .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '') // zero-width
        .replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, c =>
            ['\n', '\r', '\t'].includes(c) ? c : ''
        );
}

/**
 * Normalizes smart quotes to ASCII quotes for robust JSON parsing.
 */
function normalizeQuotesDeep(obj: any): any {
    if (typeof obj === 'string') {
        return obj
            .replace(/[“”]/g, '"')
            .replace(/[‘’]/g, "'")
            .replace(/\\"/g, "'")
            .replace(/\"/g, "'");
    } else if (Array.isArray(obj)) {
        return obj.map(normalizeQuotesDeep);
    } else if (typeof obj === 'object' && obj !== null) {
        const result: any = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                result[key] = normalizeQuotesDeep(obj[key]);
            }
        }
        return result;
    }
    return obj;
}

export function parseJSON(
    jsonString: string | any,
    swallowErrors = false
): any {

    // 1. If it's already an object (but not null), return it directly
    if (typeof jsonString === "object" && jsonString !== null) {
        return jsonString;
    }

    // 2. Handle null/undefined input or non-string/number/bool
    if (
        jsonString === null ||
        jsonString === undefined ||
        typeof jsonString === 'number' ||
        typeof jsonString === 'boolean'
    ) {
        return null;
    }

    // try a very basic parse first
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        // ignore
    }

    // 3. Ensure we're working with a string and perform SAFE cleaning.
    let text = String(jsonString).trim();

    // remove weird characters
    text = removeNonPrintableCharacters(text);

    // Restore explicit malformed ```json block check
    if (
        text.trim().startsWith("```json") &&
        !text.trim().endsWith("```")
    ) {
        const err = new Error("Malformed JSON: missing closing backticks");
        if (swallowErrors) {
            console.error("Error parsing JSON:", err);
            return null;
        }
        throw err;
    }

    // 4. Try to extract content from ```json blocks
    const jsonBlockMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonBlockMatch && jsonBlockMatch[1]) {
        let block = jsonBlockMatch[1];
        block = normalizeSmartQuotes(block);
        return tryParseWithFallbacks(block, swallowErrors);
    } else {
        text = normalizeSmartQuotes(text);
        
    }

    // 5. Find the first and last structural characters ({, [, }, ])
    const firstBracket = text.indexOf('{');
    const firstSquare = text.indexOf('[');
    let startIndex = -1;
    if (firstBracket === -1) {
        startIndex = firstSquare;
    } else if (firstSquare === -1) {
        startIndex = firstBracket;
    } else {
        startIndex = Math.min(firstBracket, firstSquare);
    }
    if (startIndex === -1) {
        // No JSON structure found
        return null;
    }
    const lastBracket = text.lastIndexOf('}');
    const lastSquare = text.lastIndexOf(']');
    const endIndex = Math.max(lastBracket, lastSquare);
    if (endIndex > startIndex) {
        let potentialJson = text.substring(startIndex, endIndex + 1);
        potentialJson = normalizeSmartQuotes(potentialJson);
        return tryParseWithFallbacks(potentialJson, swallowErrors);
    }
    return null;
}

/**
 * Removes the text that is wrapped in backticks. I.e. ```text``` or ```json``
 * Removes the text that is wrapped in backticks. I.e. ```text``` or ```json```
 *
 * @param rawText the text that is wrapped in backticks
 * @param tag the text after the backticks (e.g. 'json', 'markdown', 'typescript')
 */
export function removeBackticks(rawText: string, tag: string) {
    if (!rawText) {
        return rawText;
    }

    let rawTextTrimmed = rawText.trim();

    // check for ```tag block
    if (rawTextTrimmed.startsWith("```" + tag)) {
        rawTextTrimmed = rawTextTrimmed.substring(("```" + tag).length);
    } else if (rawTextTrimmed.startsWith("```")) {
        // Handle case where there's just ``` without a tag
        rawTextTrimmed = rawTextTrimmed.substring(3);
    }

    if (rawTextTrimmed.endsWith("```")) {
        // Remove only the last instance of ```
        const lastIndex = rawTextTrimmed.lastIndexOf("```");
        rawTextTrimmed = rawTextTrimmed.substring(0, lastIndex);
    }

    return rawTextTrimmed.trim();
}

export function parseCharacterRole(role: string): CharacterType {
    if (!role) return "unknown";

    // Normalize input: lowercase and trim
    const normalizedRole = role.toLowerCase().trim();

    // Role mappings
    const roleMap: Record<string, CharacterType> = {
        protagonist: "protagonist",
        "support character": "supporting",
        "supporting character": "supporting",
        supporting: "supporting",
        antagonist: "antagonist",
        "minor role": "minor",
        minor: "minor",
    };

    // Return mapped role or unknown
    return roleMap[normalizedRole] || "unknown";
}

/**
 * Parses a string priority value into an IssuePriority enum value.
 * Valid values are 'high', 'medium', 'low', and 'na'.
 * Any other value is considered NA (Not Applicable).
 *
 * @param priority The priority string to parse
 * @returns The corresponding IssuePriority enum value
 */
export function parseIssuePriority(priority: string): IssuePriority {
    // Handle null/undefined input
    if (!priority) {
        return IssuePriority.NA;
    }

    // Normalize input to lowercase
    const normalizedPriority = priority.toLowerCase().trim();

    // Define valid priorities
    const priorityMap: Record<string, IssuePriority> = {
        high: IssuePriority.High,
        medium: IssuePriority.Medium,
        low: IssuePriority.Low,
        na: IssuePriority.NA,
        "n/a": IssuePriority.NA,
        "not applicable": IssuePriority.NA,
    };

    // Return the mapped priority or NA for any non-standard value
    return priorityMap[normalizedPriority] || IssuePriority.NA;
}

export function parseImpactScoreFromPriority(priority: string): ImpactScore {
    const priorityMap: Record<string, number> = {
        high: 10,
        medium: 5,
        low: 1,
    };

    return parseImpactScore(priorityMap[priority?.toLowerCase()] ?? 0);
}

/**
 * Gets the correct priority label text for an issue priority.
 * Uses inverted mapping: High priority issues get "Revision recommended" (red),
 * Low priority issues get "Well-Crafted" (green).
 *
 * @param priority The issue priority enum value
 * @returns The appropriate label string
 */
export function getPriorityTextFromIssuePriority(
    priority: IssuePriority
): string {
    if (priority === IssuePriority.High) {
        return IMPACT_SCORE_LABELS.LOW.scoreString; // "Revision recommended" (RED)
    } else if (priority === IssuePriority.Medium) {
        return IMPACT_SCORE_LABELS.MEDIUM.scoreString; // "Consider revision" (YELLOW)
    } else if (priority === IssuePriority.Low) {
        return IMPACT_SCORE_LABELS.HIGH.scoreString; // "Well-Crafted" (GREEN)
    } else {
        return "N/A";
    }
}

/**
 * Gets the correct priority color classes for an issue priority.
 * Uses inverted mapping: High priority issues get red colors,
 * Low priority issues get green colors.
 *
 * @param priority The issue priority enum value
 * @returns CSS class string for background and text colors
 */
export function getPriorityColorsFromIssuePriority(
    priority: IssuePriority
): string {
    if (priority === IssuePriority.High) {
        return `${IMPACT_SCORE_LABELS.LOW.newPriorityColorBackground} ${IMPACT_SCORE_LABELS.LOW.newPriorityColor}`;
    } else if (priority === IssuePriority.Medium) {
        return `${IMPACT_SCORE_LABELS.MEDIUM.newPriorityColorBackground} ${IMPACT_SCORE_LABELS.MEDIUM.newPriorityColor}`;
    } else if (priority === IssuePriority.Low) {
        return `${IMPACT_SCORE_LABELS.HIGH.newPriorityColorBackground} ${IMPACT_SCORE_LABELS.HIGH.newPriorityColor}`;
    } else {
        return "bg-gray-100 text-gray-500"; // Default for NA
    }
}

export function getPriorityValue(priority: IssuePriority | string): number {
    const priorityMap: Record<string, number> = {
        high: 3,
        medium: 2,
        low: 1,
    };

    if (typeof priority === "number") {
        // Handle enum values
        if (priority === IssuePriority.High) return 3;
        if (priority === IssuePriority.Medium) return 2;
        if (priority === IssuePriority.Low) return 1;
        return 0;
    }

    return priorityMap[priority?.toLowerCase()] ?? 0;
}

/**
 * Filters out metrics with NA values.
 * This function is used by both the AnalysisPage and LeftSidebar components
 * to ensure consistent filtering of metrics.
 *
 * @param metrics Array of metrics to filter
 * @returns Array of metrics with NA values filtered out
 */
export function filterOutNAMetrics<T extends { value: any; label?: string }>(
    metrics: T[]
): T[] {
    console.log(`[filterOutNAMetrics] Input metrics count:`, metrics.length);

    // Define the valid values we want to keep
    const validValues = ["high", "medium", "low"];

    const filteredMetrics = metrics.filter((metric) => {
        // Skip null or undefined values
        if (metric.value === null || metric.value === undefined) {
            console.log(
                `[filterOutNAMetrics] Filtering out null/undefined metric:`,
                metric
            );
            return false;
        }

        // Convert to string and lowercase for consistent comparison
        const valueStr = String(metric.value).toLowerCase();

        // Check if the value is in our list of valid values
        const isValid = validValues.includes(valueStr);

        if (!isValid) {
            console.log(
                `[filterOutNAMetrics] Filtering out non-standard metric:`,
                {
                    value: metric.value,
                    label: metric.label,
                }
            );
        }

        // Only keep metrics with valid values
        return isValid;
    });

    console.log(
        `[filterOutNAMetrics] Output metrics count:`,
        filteredMetrics.length
    );
    console.log(
        `[filterOutNAMetrics] Removed ${
            metrics.length - filteredMetrics.length
        } non-standard metrics`
    );

    return filteredMetrics;
}

/**
 * @param score The numerical impact score (e.g., 1-10).
 * @returns An ImpactScore object containing the score, explanation, and priority details.
 */
export function parseImpactScore(score: number): ImpactScore {
    let explanation: string;
    let priorityColor: string;
    let priorityColorBackground: string;
    let priorityColorBorder: string;
    let scoreString: string;

    // Determine priority based on score
    if (score >= 7) {
        explanation = IMPACT_SCORE_LABELS.HIGH.explanation;
        priorityColor = IMPACT_SCORE_LABELS.HIGH.priorityColor;
        scoreString = IMPACT_SCORE_LABELS.HIGH.scoreString;
        priorityColorBackground =
            IMPACT_SCORE_LABELS.HIGH.priorityColorBackground;
        priorityColorBorder = IMPACT_SCORE_LABELS.HIGH.priorityColorBorder;
    } else if (score >= 4) {
        explanation = IMPACT_SCORE_LABELS.MEDIUM.explanation;
        priorityColor = IMPACT_SCORE_LABELS.MEDIUM.priorityColor;
        scoreString = IMPACT_SCORE_LABELS.MEDIUM.scoreString;
        priorityColorBackground =
            IMPACT_SCORE_LABELS.MEDIUM.priorityColorBackground;
        priorityColorBorder = IMPACT_SCORE_LABELS.MEDIUM.priorityColorBorder;
    } else if (score >= 1) {
        explanation = IMPACT_SCORE_LABELS.LOW.explanation;
        priorityColor = IMPACT_SCORE_LABELS.LOW.priorityColor;
        scoreString = IMPACT_SCORE_LABELS.LOW.scoreString;
        priorityColorBackground =
            IMPACT_SCORE_LABELS.LOW.priorityColorBackground;
        priorityColorBorder = IMPACT_SCORE_LABELS.LOW.priorityColorBorder;
    } else {
        // Handle scores less than 1 or invalid scores as N/A
        explanation = IMPACT_SCORE_LABELS.NA.explanation;
        priorityColor = IMPACT_SCORE_LABELS.NA.priorityColor;
        scoreString = IMPACT_SCORE_LABELS.NA.scoreString;
        priorityColorBackground =
            IMPACT_SCORE_LABELS.NA.priorityColorBackground;
        priorityColorBorder = IMPACT_SCORE_LABELS.NA.priorityColorBorder;
    }

    // Return the ImpactScore object
    return {
        score: score,
        explanation: explanation,
        priorityColor: priorityColor,
        scoreString: scoreString,
        priorityColorBackground: priorityColorBackground,
        priorityColorBorder: priorityColorBorder,
    } as ImpactScore;
}

/**
 * Generates dynamic colorful bar data from an array of issues with priorities.
 * Uses correct priority-to-color mapping: High priority issues show as red,
 * Low priority issues show as green.
 *
 * @param issues Array of issues with priority properties
 * @returns Array of bar data for visualization, or null if no valid issues
 */
export const getDynamicColorfulBarData = (
    issues: Array<{ priority: IssuePriority }> | null | undefined
): BarDataProps[] | null => {
    if (!issues || issues.length === 0) {
        return null;
    }

    const highPriorityCount = issues.filter(
        (issue) => issue.priority === IssuePriority.High
    ).length;

    const mediumPriorityCount = issues.filter(
        (issue) => issue.priority === IssuePriority.Medium
    ).length;

    const lowPriorityCount = issues.filter(
        (issue) => issue.priority === IssuePriority.Low
    ).length;

    const totalCount =
        highPriorityCount + mediumPriorityCount + lowPriorityCount;

    if (totalCount === 0) {
        return null;
    }

    return [
        {
            label: IMPACT_SCORE_LABELS.LOW.scoreString, // "Revision recommended" for HIGH priority
            percentage: Math.round((highPriorityCount / totalCount) * 100),
            impactLevel: "LOW" as const,
        },
        {
            label: IMPACT_SCORE_LABELS.MEDIUM.scoreString, // "Consider revision" for MEDIUM priority
            percentage: Math.round((mediumPriorityCount / totalCount) * 100),
            impactLevel: "MEDIUM" as const,
        },
        {
            label: IMPACT_SCORE_LABELS.HIGH.scoreString, // "Well-Crafted" for LOW priority
            percentage: Math.round((lowPriorityCount / totalCount) * 100),
            impactLevel: "HIGH" as const,
        },
    ].filter((item) => item.percentage > 0);
};

export const createAreaId = (area: string): string => {
    return area
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "-");
};

/**
 * Calculates priority counts from an array of issues.
 * 
 * @param issues Array of issues with priority properties
 * @returns Object containing counts for each priority level and total
 */
export const calculatePriorityCounts = (issues: Issue[] | Array<{ priority: IssuePriority }>) => {
    if (!issues || issues.length === 0) {
        return { highCount: 0, mediumCount: 0, lowCount: 0, total: 0 };
    }

    const highCount = issues.filter(issue => issue.priority === IssuePriority.High).length;
    const mediumCount = issues.filter(issue => issue.priority === IssuePriority.Medium).length;
    const lowCount = issues.filter(issue => issue.priority === IssuePriority.Low).length;
    const total = highCount + mediumCount + lowCount;
    
    return { highCount, mediumCount, lowCount, total };
};

/**
 * Gets the appropriate CSS background class for a given priority level.
 * Uses inverted mapping: High priority issues get red, Low priority issues get green.
 * 
 * @param priority The issue priority enum value
 * @returns CSS background class string
 */
export const getPriorityBackgroundClass = (priority: IssuePriority): string => {
    switch (priority) {
        case IssuePriority.High:
            return "bg-red-500";
        case IssuePriority.Medium:
            return "bg-amber-500";
        case IssuePriority.Low:
            return "bg-green-500";
        default:
            return "bg-gray-500";
    }
};

/**
 * Gets the appropriate CSS text color class for a given priority level.
 * Uses inverted mapping: High priority issues get red, Low priority issues get green.
 * 
 * @param priority The issue priority enum value
 * @returns CSS text color class string
 */
export const getPriorityTextClass = (priority: IssuePriority): string => {
    switch (priority) {
        case IssuePriority.High:
            return "text-red-500";
        case IssuePriority.Medium:
            return "text-amber-500";
        case IssuePriority.Low:
            return "text-green-500";
        default:
            return "text-gray-500";
    }
};

/**
 * Generates priority bar data for chapter cards in the roadmap.
 * Creates the data structure needed for the small progress bars on chapter cards.
 * 
 * @param issues Array of issues with priority properties
 * @returns Array of bar data with percentages and colors, or empty array if no issues
 */
export const generateChapterPriorityBarData = (issues: Issue[] | Array<{ priority: IssuePriority }>) => {
    const { highCount, mediumCount, lowCount, total } = calculatePriorityCounts(issues);
    
    if (total === 0) return [];
    
    return [
        {
            label: "Needs Improvement",
            count: highCount,
            percentage: (highCount / total) * 100,
            color: "red",
            priority: IssuePriority.High
        },
        {
            label: "Consider Revising",
            count: mediumCount,
            percentage: (mediumCount / total) * 100,
            color: "orange", 
            priority: IssuePriority.Medium
        },
        {
            label: "Working well",
            count: lowCount,
            percentage: (lowCount / total) * 100,
            color: "green",
            priority: IssuePriority.Low
        },
    ].filter(item => item.count > 0);
};

/**
 * Maps old color strings to impact levels for backwards compatibility.
 * Used in roadmap chapter cards for consistent color mapping.
 */
export const COLOR_TO_IMPACT_LEVEL: Record<string, keyof typeof IMPACT_SCORE_LABELS> = {
    red: "LOW",      // High priority issues -> red -> "LOW" impact level (inverted)
    orange: "MEDIUM", // Medium priority issues -> orange -> "MEDIUM" impact level  
    green: "HIGH",   // Low priority issues -> green -> "HIGH" impact level (inverted)
};
